import {
  CustomAuthPublicClientApplication,
  type ICustomAuthPublicClientApplication,
  type SignInCodeRequiredState,
  type SignInResult,
  type SignUpCodeRequiredState,
  type SignUpResult,
  type CustomAuthAccountData,
  type SignInSubmitCodeResult,
  type SignUpSubmitCodeResult,
} from '@azure/msal-browser/custom-auth';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { queryClient } from '@accio3d/shared-ui/services/queryClient';
import type { OrgType } from '@accio3d/shared-ui/utils/enums';

export const AUTH_SCOPES = ['openid', 'offline_access', 'api://accio3d-api/access_as_user'];

const REFRESH_TOKEN_KEY = 'app_auth_refresh_token';
const ACTIVE_ORG_KEY = 'app_auth_organization_id';
const LAST_ORG_KEY = 'app_auth_last_organization_id';
const AUTH_SESSION_UPDATED_EVENT = 'accio:auth-session-updated';
const SESSION_REFRESH_MARGIN_MS = 60_000;
/** Max delay `setTimeout` accepts (32-bit signed ms) before it overflows and fires immediately. */
const MAX_SETTIMEOUT_DELAY_MS = 2_147_483_000;

/** Cached Entra account cannot mint an access token. */
export class EntraTokenError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EntraTokenError';
  }
}

/** POST /auth/me failed. */
export class SessionExchangeError extends Error {
  /** HTTP status, or `null` if no response. */
  readonly status: number | null;

  constructor(message: string, status: number | null) {
    super(message);
    this.name = 'SessionExchangeError';
    this.status = status;
  }
}

/** True for a dead Entra account (token error or 401). Transient failures are not. */
export function isAccountUnusable(error: unknown): boolean {
  if (error instanceof EntraTokenError) return true;
  return error instanceof SessionExchangeError && error.status === 401;
}

export function requireEnv(value: string | undefined, key: string, reason: string): string {
  if (!value) throw new Error(`Missing required env var: ${key} must be set for ${reason}.`);
  return value;
}

export function createNativeAuth() {
  const apiBaseUrl = requireEnv(
    import.meta.env.VITE_API_BASE_URL,
    'VITE_API_BASE_URL',
    'API calls',
  ).replace(/\/$/, '');
  const clientId = requireEnv(
    import.meta.env.VITE_ENTRA_CLIENT_ID,
    'VITE_ENTRA_CLIENT_ID',
    'native-auth initialization',
  );
  const tenantId = requireEnv(
    import.meta.env.VITE_ENTRA_TENANT_ID,
    'VITE_ENTRA_TENANT_ID',
    'native-auth authority',
  );
  const tenantSubdomain = requireEnv(
    import.meta.env.VITE_ENTRA_TENANT_SUBDOMAIN,
    'VITE_ENTRA_TENANT_SUBDOMAIN',
    'native-auth authority',
  );

  const authorityUrl = `https://${tenantSubdomain}.ciamlogin.com/${tenantId}/v2.0`;
  const authApiProxyUrl = `${apiBaseUrl}/api/v1/native-auth`;

  let clientPromise: Promise<ICustomAuthPublicClientApplication> | null = null;
  const getClient = () => {
    if (!clientPromise) {
      clientPromise = CustomAuthPublicClientApplication.create({
        auth: {
          clientId,
          authority: authorityUrl,
          knownAuthorities: [new URL(authorityUrl).host],
        },
        cache: {
          cacheLocation: 'localStorage',
        },
        customAuth: {
          authApiProxyUrl: authApiProxyUrl,
          challengeTypes: ['oob', 'redirect'],
        },
      }).catch((error) => {
        clientPromise = null;
        throw error;
      });
    }
    return clientPromise;
  };

  return { getClient, scopes: AUTH_SCOPES };
}

export function createAuthService(apiBaseUrl: string) {
  let currentToken: string | null = null;
  let currentSession: AuthSession | null = null;

  const url = (path: string) => `${apiBaseUrl}/api/v1${path}`;

  const persistSession = (data: LoginResponse, token: string) => {
    currentToken = token;
    localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
    if (data.organization?.id) {
      sessionStorage.setItem(ACTIVE_ORG_KEY, data.organization.id);
      localStorage.setItem(LAST_ORG_KEY, data.organization.id);
    } else {
      sessionStorage.removeItem(ACTIVE_ORG_KEY);
    }
    currentSession = {
      user: data.user,
      organization: data.organization,
      membership: data.membership,
      availableOrganizations: data.availableOrganizations ?? [],
      pendingInvitations: data.pendingInvitations ?? [],
      expiresAt: data.expiresAt,
    };
    window.dispatchEvent(new Event(AUTH_SESSION_UPDATED_EVENT));
  };

  const clear = () => {
    currentToken = null;
    currentSession = null;
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(ACTIVE_ORG_KEY);
    localStorage.removeItem(LAST_ORG_KEY);
    window.dispatchEvent(new Event(AUTH_SESSION_UPDATED_EVENT));
  };

  const factory = {
    getToken: () => currentToken,
    setToken: (token: string) => {
      currentToken = token;
    },
    getSession: () => currentSession,
    setSession: (session: AuthSession) => {
      currentSession = session;
    },
    clear,

    async restoreSession(entraToken: string): Promise<LoginResponse> {
      // Silent restore: rotate the stored refresh token and re-establish the session
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!refreshToken) throw new Error('No refresh token available');

      // A fresh tab should re-enter the same organization.
      const organizationId =
        sessionStorage.getItem(ACTIVE_ORG_KEY) ?? localStorage.getItem(LAST_ORG_KEY);

      const response = await fetch(url('/auth/restore'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${entraToken}` },
        body: JSON.stringify({ refreshToken, ...(organizationId ? { organizationId } : {}) }),
      });
      if (!response.ok) throw new SessionExchangeError('Session restore failed', response.status);
      const data = (await response.json()) as LoginResponse;
      persistSession(data, entraToken);
      return data;
    },

    async login(entraToken: string): Promise<LoginResponse> {
      // Prefer this tab's active org; fall back to the most recently used org
      // anywhere so a fresh tab re-enters the same organization.
      const organizationId =
        sessionStorage.getItem(ACTIVE_ORG_KEY) ?? localStorage.getItem(LAST_ORG_KEY);

      const exchange = (sendOrgId: string | null) =>
        fetch(url('/auth/me'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${entraToken}` },
          ...(sendOrgId ? { body: JSON.stringify({ organizationId: sendOrgId }) } : {}),
        });

      let response = await exchange(organizationId);

      // A stored org id can outlive the membership (e.g. revoked while signed
      // in). Drop it and re-exchange without a selection so the user lands on
      // the organization picker instead of being signed out by bootstrap.
      if (response.status === 403 && organizationId) {
        sessionStorage.removeItem(ACTIVE_ORG_KEY);
        localStorage.removeItem(LAST_ORG_KEY);
        response = await exchange(null);
      }

      if (!response.ok) throw new SessionExchangeError('Login failed', response.status);
      const data = (await response.json()) as LoginResponse;
      persistSession(data, entraToken);
      return data;
    },

    async register(
      entraToken: string,
      payload: {
        organization?: {
          name: string;
          type?: string;
          website?: string;
          industry?: string;
          dunsNumber?: string;
        };
        invitationToken?: string;
        profile?: { firstName?: string; lastName?: string };
      },
    ): Promise<LoginResponse> {
      const response = await fetch(url('/auth/register'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${entraToken}` },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('Registration failed');
      const data = (await response.json()) as LoginResponse;
      persistSession(data, entraToken);
      return data;
    },

    async refresh(entraToken: string): Promise<void> {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!refreshToken) throw new Error('No refresh token available');

      const response = await fetch(url('/auth/refresh'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(entraToken ? { Authorization: `Bearer ${entraToken}` } : {}),
        },
        body: JSON.stringify({ refreshToken }),
      });
      // Do not clear() here: a failed /auth/refresh can be transient (network
      // blip, 5xx), and the caller falls back to /auth/me before deciding
      // whether the session is actually unusable. Clearing eagerly would drop
      // a still-valid session out from under that fallback.
      if (!response.ok) {
        throw new SessionExchangeError('Failed to refresh token', response.status);
      }

      const data = (await response.json()) as { refreshToken: string; expiresAt: string };
      localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
      if (currentSession) {
        currentSession = { ...currentSession, expiresAt: data.expiresAt };
        window.dispatchEvent(new Event(AUTH_SESSION_UPDATED_EVENT));
      }
    },

    async switchOrganization(organizationId: string): Promise<LoginResponse> {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      const accessToken = currentToken;
      if (!refreshToken || !accessToken) throw new Error('No session');

      const response = await fetch(url('/auth/switch-organization'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          ...(currentSession?.organization?.id
            ? { 'X-Organization-Id': currentSession.organization.id }
            : {}),
        },
        body: JSON.stringify({ organizationId, refreshToken }),
      });
      if (!response.ok) throw new Error('Failed to switch organization');
      const data = (await response.json()) as LoginResponse;
      persistSession(data, accessToken);
      return data;
    },

    async acceptInvitation(invitationId: string): Promise<LoginResponse> {
      const accessToken = currentToken;
      if (!accessToken) throw new Error('No access token');

      const response = await fetch(url('/auth/accept-invitation'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ invitationId }),
      });
      if (!response.ok) throw new Error('Failed to accept invitation');
      const data = (await response.json()) as LoginResponse;
      persistSession(data, accessToken);
      return data;
    },

    async logout(entraToken?: string): Promise<void> {
      const accessToken = entraToken ?? currentToken;
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      try {
        if (accessToken) {
          await fetch(url('/auth/logout'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
            body: JSON.stringify(refreshToken ? { refreshToken } : {}),
          });
        }
      } catch {
        /* best-effort */
      }
      clear();
    },
  };

  // Single-flight slot for page-load session establishment (see ensureSession).
  let sessionPromise: Promise<LoginResponse> | null = null;

  return {
    ...factory,

    async ensureSession(entraToken: string): Promise<LoginResponse> {
      // concurrent restores should not race the server-side token rotation
      // and push the loser back onto the login path.
      if (!sessionPromise) {
        sessionPromise = (async () => {
          try {
            return await factory.restoreSession(entraToken);
          } catch {
            return await factory.login(entraToken);
          }
        })().finally(() => {
          sessionPromise = null;
        });
      }
      return sessionPromise;
    },
  };
}

// ── Types ────────────────────────────────────────────────

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
  jobTitle: string | null;
  about: string | null;
  phone: string | null;
  linkedinUrl: string | null;
}

export interface AuthOrganization {
  id: string;
  name: string;
  type: OrgType;
  website?: string | null;
  industry?: string | null;
  dunsNumber?: string | null;
  tier?: string | null;
}

export interface AuthMembership {
  id: string;
  role: 'admin' | 'member' | 'viewer';
}

export interface AvailableOrganization {
  id: string;
  membershipId: string;
  role: string;
  name: string;
  type: string;
}

export interface PendingInvitation {
  id: string;
  organizationId: string;
  organizationName: string;
  invitedByEmail: string;
  role: string;
  expiresAt: string;
}

interface LoginResponse {
  refreshToken: string;
  expiresAt: string;
  user: AuthUser;
  organization: AuthOrganization | null;
  membership: AuthMembership | null;
  availableOrganizations: AvailableOrganization[];
  pendingInvitations: PendingInvitation[];
}

interface AuthSession {
  user: AuthUser;
  organization: AuthOrganization | null;
  membership: AuthMembership | null;
  availableOrganizations: AvailableOrganization[];
  pendingInvitations: PendingInvitation[];
  expiresAt: string;
}

// ── Auth Context ─────────────────────────────────────────

export interface AuthContextType {
  user: AuthUser | null;
  organization: AuthOrganization | null;
  membership: AuthMembership | null;
  availableOrganizations: AvailableOrganization[];
  pendingInvitations: PendingInvitation[];
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  requiresOnboarding: boolean;
  login: () => void;
  registerOrganization: (
    org: { name: string; type?: string; website?: string; industry?: string; dunsNumber?: string },
    profile?: { firstName?: string; lastName?: string },
  ) => Promise<void>;
  switchOrganization: (organizationId: string) => Promise<void>;
  acceptInvitation: (invitationId: string) => Promise<void>;
  logout: () => Promise<void>;
  acquireEntraToken: (forceRefresh?: boolean) => Promise<string>;
  /** Merge a partial user into the current session (e.g. after a profile update). */
  updateUser: (user: Partial<AuthUser>) => void;
  /** Merge a partial organization into the current session (e.g. after an org update). */
  updateOrganizationInfo: (organization: Partial<AuthOrganization>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ── Native Auth Context ──────────────────────────────────

type NativeAuthFlowState =
  | { kind: 'idle' }
  | { kind: 'signInOtp'; email: string; state: SignInCodeRequiredState }
  | { kind: 'signUpOtp'; email: string; state: SignUpCodeRequiredState }
  | { kind: 'exchanging' }
  | { kind: 'post_otp'; email: string };

export interface NativeAuthContextType {
  flow: NativeAuthFlowState;
  startSignIn: (email: string) => Promise<'otp' | 'user_not_found' | 'invalid_email' | 'completed'>;
  startSignUp: (
    email: string,
    attrs: { givenName: string; surname: string },
  ) => Promise<'otp' | 'user_exists' | 'invalid_email'>;
  submitOtp: (
    code: string,
  ) => Promise<'authenticated' | 'needs_organization' | 'pick_organization' | 'invalid_code'>;
  resendOtp: () => Promise<void>;
  cancelFlow: () => void;
}

const NativeAuthContext = createContext<NativeAuthContextType | undefined>(undefined);

// ── Auth Provider ────────────────────────────────────────

export function AuthProvider({ children }: PropsWithChildren) {
  const { getClient, scopes } = useMemo(() => createNativeAuth(), []);
  const apiBaseUrl = useMemo(
    () =>
      requireEnv(import.meta.env.VITE_API_BASE_URL, 'VITE_API_BASE_URL', 'API calls').replace(
        /\/$/,
        '',
      ),
    [],
  );
  const authService = useMemo(() => createAuthService(apiBaseUrl), [apiBaseUrl]);

  const [session, setSession] = useState<AuthSession | null>(() => authService.getSession());
  const [token, setToken] = useState<string | null>(() => authService.getToken());
  const [loading, setLoading] = useState(true);
  const [flow, setFlow] = useState<NativeAuthFlowState>({ kind: 'idle' });
  const accountRef = useRef<CustomAuthAccountData | null>(null);

  const getCurrentAccountData = useCallback(async (): Promise<CustomAuthAccountData | null> => {
    const client = await getClient();
    const result = client.getCurrentAccount();
    if (result.isCompleted() && result.data) {
      accountRef.current = result.data;
      return result.data;
    }
    accountRef.current = null;
    return null;
  }, [getClient]);

  /** Pass `forceRefresh` after a 401 so MSAL doesn't return the rejected token. */
  const acquireEntraToken = useCallback(
    async (forceRefresh = false): Promise<string> => {
      if (accountRef.current) {
        const result = await accountRef.current.getAccessToken({ forceRefresh, scopes });
        if (result.isCompleted() && result.data?.accessToken) return result.data.accessToken;
      }
      const account = await getCurrentAccountData();
      if (!account) throw new EntraTokenError('No active native-auth account');
      const result = await account.getAccessToken({ forceRefresh, scopes });
      if (!result.isCompleted() || !result.data?.accessToken) {
        throw new EntraTokenError('Failed to acquire native-auth access token');
      }
      return result.data.accessToken;
    },
    [getCurrentAccountData, scopes],
  );

  const persistSession = useCallback(
    (nextSession: AuthSession, nextToken: string) => {
      authService.setToken(nextToken);
      authService.setSession(nextSession);
      setSession(nextSession);
      setToken(nextToken);
    },
    [authService],
  );

  const toAuthSession = (loginResponse: LoginResponse): AuthSession => ({
    user: loginResponse.user,
    organization: loginResponse.organization,
    membership: loginResponse.membership,
    availableOrganizations: loginResponse.availableOrganizations ?? [],
    pendingInvitations: loginResponse.pendingInvitations ?? [],
    expiresAt: loginResponse.expiresAt,
  });

  const exchangeEntraForPlatformSession = useCallback(async (): Promise<AuthSession> => {
    const entraToken = await acquireEntraToken();
    const loginResponse = await authService.login(entraToken);
    const nextSession = toAuthSession(loginResponse);
    persistSession(nextSession, entraToken);
    return nextSession;
  }, [acquireEntraToken, authService, persistSession]);

  const restorePlatformSession = useCallback(async (): Promise<AuthSession> => {
    const entraToken = await acquireEntraToken();
    const loginResponse = await authService.ensureSession(entraToken);
    const nextSession = toAuthSession(loginResponse);
    persistSession(nextSession, entraToken);
    return nextSession;
  }, [acquireEntraToken, authService, persistSession]);

  /** Clears the platform session and signs out a dead Entra account (best-effort). */
  const signOutDeadAccount = useCallback(async () => {
    authService.clear();
    const staleAccount = accountRef.current ?? (await getCurrentAccountData());
    if (staleAccount) {
      try {
        await staleAccount.signOut();
      } catch {
        /* best-effort */
      }
    }
    accountRef.current = null;
  }, [authService, getCurrentAccountData]);

  useEffect(() => {
    const syncSession = () => {
      setSession(authService.getSession());
      setToken(authService.getToken());
    };
    window.addEventListener(AUTH_SESSION_UPDATED_EVENT, syncSession);
    return () => window.removeEventListener(AUTH_SESSION_UPDATED_EVENT, syncSession);
  }, [authService]);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      const stored = authService.getSession();
      const storedToken = authService.getToken();
      const account = await getCurrentAccountData();

      if (!account) {
        authService.clear();
        if (!cancelled) {
          setSession(null);
          setToken(null);
          setLoading(false);
        }
        return;
      }

      if (stored && storedToken && Date.now() < new Date(stored.expiresAt).getTime()) {
        if (!cancelled) {
          setSession(stored);
          setToken(storedToken);
          setLoading(false);
        }
        return;
      }

      try {
        await restorePlatformSession();
      } catch (error) {
        // oxlint-disable-next-line no-console
        console.error('[auth] bootstrap failed:', error);

        // Sign out only a dead account; keep the MSAL cache on transient failures.
        if (isAccountUnusable(error)) await signOutDeadAccount();

        if (!cancelled) {
          setSession(null);
          setToken(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [authService, getCurrentAccountData, restorePlatformSession, signOutDeadAccount]);

  /** Rotates the platform session's refresh token while the Entra session is still live. */
  const refreshSession = useCallback(async () => {
    try {
      const entraToken = await acquireEntraToken();
      await authService.refresh(entraToken);
    } catch (error) {
      // oxlint-disable-next-line no-console
      console.error('[auth] silent session refresh failed:', error);

      try {
        await exchangeEntraForPlatformSession();
      } catch (fallbackError) {
        // oxlint-disable-next-line no-console
        console.error('[auth] session refresh fallback failed:', fallbackError);

        if (isAccountUnusable(fallbackError)) {
          await signOutDeadAccount();
          setSession(null);
          setToken(null);
          return;
        }

        // Both the refresh and its fallback were transient failures (network,
        // 5xx). The existing session may still be valid — keep it and retry
        // shortly instead of signing the user out.
        const current = authService.getSession();
        if (current && Date.now() < new Date(current.expiresAt).getTime()) {
          window.setTimeout(() => void refreshSession(), 30_000);
        }
      }
    }
  }, [acquireEntraToken, authService, exchangeEntraForPlatformSession, signOutDeadAccount]);

  useEffect(() => {
    if (!session) return;

    // setTimeout only accepts a 32-bit signed delay; a distant expiresAt
    // would otherwise overflow and fire almost immediately. Re-check in
    // capped increments instead of scheduling the full delay up front.
    let timeoutId: ReturnType<typeof setTimeout>;
    const scheduleCheck = () => {
      const remaining =
        new Date(session.expiresAt).getTime() - Date.now() - SESSION_REFRESH_MARGIN_MS;
      timeoutId =
        remaining <= MAX_SETTIMEOUT_DELAY_MS
          ? setTimeout(refreshSession, Math.max(remaining, 0))
          : setTimeout(scheduleCheck, MAX_SETTIMEOUT_DELAY_MS);
    };
    scheduleCheck();
    return () => clearTimeout(timeoutId);
  }, [session, refreshSession]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || !session) return;
      if (Date.now() >= new Date(session.expiresAt).getTime() - SESSION_REFRESH_MARGIN_MS) {
        void refreshSession();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [session, refreshSession]);

  const startSignIn = useCallback<NativeAuthContextType['startSignIn']>(
    async (email) => {
      const trimmed = email.trim();
      if (!trimmed) return 'invalid_email';

      const client = await getClient();
      let signInRaw = await client.signIn({ username: trimmed, scopes });

      if (
        signInRaw.isFailed() &&
        signInRaw.error?.errorData?.errorDescription?.toLowerCase().includes('already signed in')
      ) {
        const stale = accountRef.current;
        if (stale) {
          try {
            await stale.signOut();
          } catch {
            /* best-effort */
          }
          accountRef.current = null;
        }
        signInRaw = await client.signIn({ username: trimmed, scopes });
      }

      if (signInRaw.isFailed()) {
        if (signInRaw.error?.isUserNotFound()) return 'user_not_found';
        if (signInRaw.error?.isInvalidUsername()) return 'invalid_email';
        throw new Error(signInRaw.error?.errorData?.errorDescription ?? 'Sign-in failed');
      }

      const signIn = signInRaw as SignInResult;
      if (signIn.isCodeRequired()) {
        setFlow({ kind: 'signInOtp', email: trimmed, state: signIn.state });
        return 'otp';
      }
      if (signIn.isCompleted()) {
        await exchangeEntraForPlatformSession();
        return 'completed';
      }

      throw new Error('Unexpected sign-in state');
    },
    [getClient, exchangeEntraForPlatformSession],
  );

  const startSignUp = useCallback<NativeAuthContextType['startSignUp']>(
    async (email, attrs) => {
      const trimmed = email.trim();
      if (!trimmed) return 'invalid_email';

      const client = await getClient();
      const signUpRaw = await client.signUp({
        username: trimmed,
        attributes: { givenName: attrs.givenName, surname: attrs.surname },
      });

      if (signUpRaw.isFailed()) {
        if (signUpRaw.error?.isUserAlreadyExists()) return 'user_exists';
        if (signUpRaw.error?.isInvalidUsername()) return 'invalid_email';
        throw new Error(signUpRaw.error?.errorData?.errorDescription ?? 'Sign-up failed');
      }

      const signUp = signUpRaw as SignUpResult;
      if (signUp.isCodeRequired()) {
        setFlow({ kind: 'signUpOtp', email: trimmed, state: signUp.state });
        return 'otp';
      }

      throw new Error('Unexpected sign-up state');
    },
    [getClient],
  );

  const submitOtp = useCallback<NativeAuthContextType['submitOtp']>(
    async (code) => {
      let otpEmail = '';

      if (flow.kind === 'signInOtp') {
        otpEmail = flow.email;
        const siRaw = await flow.state.submitCode(code);
        if (siRaw.isFailed()) {
          if (siRaw.error?.isInvalidCode()) return 'invalid_code';
          throw new Error(siRaw.error?.errorData?.errorDescription ?? 'Code verification failed');
        }
        const siCode = siRaw as SignInSubmitCodeResult;
        if (!siCode.isCompleted()) throw new Error('Sign-in did not complete');
      } else if (flow.kind === 'signUpOtp') {
        otpEmail = flow.email;
        const suRaw = await flow.state.submitCode(code);
        if (suRaw.isFailed()) {
          if (suRaw.error?.isInvalidCode()) return 'invalid_code';
          throw new Error(suRaw.error?.errorData?.errorDescription ?? 'Code verification failed');
        }
        const suCode = suRaw as SignUpSubmitCodeResult;
        if (!suCode.isCompleted()) throw new Error('Sign-up did not complete');
        const continuation = await suCode.state.signIn({ scopes });
        if (!continuation.isCompleted())
          throw new Error('Post-registration sign-in did not complete');
      } else {
        throw new Error('No OTP flow in progress');
      }

      setFlow({ kind: 'exchanging' });

      try {
        const nextSession = await exchangeEntraForPlatformSession();
        setFlow({ kind: 'idle' });
        if (nextSession.organization) return 'authenticated';
        return 'pick_organization';
      } catch (error) {
        setFlow({ kind: 'post_otp', email: otpEmail });
        throw error;
      }
    },
    [flow, exchangeEntraForPlatformSession],
  );

  const resendOtp = useCallback<NativeAuthContextType['resendOtp']>(async () => {
    if (flow.kind === 'signInOtp') {
      const siResendRaw = await flow.state.resendCode();
      if (siResendRaw.isFailed()) throw new Error('Failed to resend code');
      const siResend = siResendRaw as {
        isCodeRequired: () => boolean;
        state: SignInCodeRequiredState;
      };
      if (siResend.isCodeRequired()) setFlow({ ...flow, state: siResend.state });
    } else if (flow.kind === 'signUpOtp') {
      const suResendRaw = await flow.state.resendCode();
      if (suResendRaw.isFailed()) throw new Error('Failed to resend code');
      const suResend = suResendRaw as {
        isCodeRequired: () => boolean;
        state: SignUpCodeRequiredState;
      };
      if (suResend.isCodeRequired()) setFlow({ ...flow, state: suResend.state });
    } else {
      throw new Error('No OTP flow in progress');
    }
  }, [flow]);

  const cancelFlow = useCallback(() => setFlow({ kind: 'idle' }), []);

  const registerOrganization = useCallback<AuthContextType['registerOrganization']>(
    async (organization, profile) => {
      const entraToken = await acquireEntraToken();
      const loginResponse = await authService.register(entraToken, { organization, profile });
      const nextSession: AuthSession = {
        user: loginResponse.user,
        organization: loginResponse.organization,
        membership: loginResponse.membership,
        availableOrganizations: loginResponse.availableOrganizations ?? [],
        pendingInvitations: loginResponse.pendingInvitations ?? [],
        expiresAt: loginResponse.expiresAt,
      };
      persistSession(nextSession, entraToken);
    },
    [acquireEntraToken, authService, persistSession],
  );

  const switchOrganization = useCallback<AuthContextType['switchOrganization']>(
    async (organizationId) => {
      const loginResponse = await authService.switchOrganization(organizationId);
      const entraToken = authService.getToken();
      if (!entraToken) throw new Error('No access token available');
      const nextSession: AuthSession = {
        user: loginResponse.user,
        organization: loginResponse.organization,
        membership: loginResponse.membership,
        availableOrganizations: loginResponse.availableOrganizations ?? [],
        pendingInvitations: loginResponse.pendingInvitations ?? [],
        expiresAt: loginResponse.expiresAt,
      };
      persistSession(nextSession, entraToken);
      queryClient.clear();
    },
    [authService, persistSession],
  );

  const acceptInvitation = useCallback<AuthContextType['acceptInvitation']>(
    async (invitationId) => {
      const entraToken = authService.getToken();
      if (!entraToken) throw new Error('No access token available');
      const loginResponse = await authService.acceptInvitation(invitationId);
      const nextSession: AuthSession = {
        user: loginResponse.user,
        organization: loginResponse.organization,
        membership: loginResponse.membership,
        availableOrganizations: loginResponse.availableOrganizations ?? [],
        pendingInvitations: loginResponse.pendingInvitations ?? [],
        expiresAt: loginResponse.expiresAt,
      };
      persistSession(nextSession, entraToken);
      queryClient.clear();
    },
    [authService, persistSession],
  );

  const logout = useCallback<AuthContextType['logout']>(async () => {
    await authService.logout();
    const account = accountRef.current;
    if (account) {
      try {
        await account.signOut();
      } catch {
        /* best-effort */
      }
    }
    authService.clear();
    accountRef.current = null;
    setSession(null);
    setToken(null);
    setFlow({ kind: 'idle' });
  }, [authService]);

  const login = useCallback<AuthContextType['login']>(() => {
    setFlow({ kind: 'idle' });
  }, []);

  const updateUser = useCallback<AuthContextType['updateUser']>(
    (partial) => {
      const current = authService.getSession();
      if (!current) return;
      const next: AuthSession = { ...current, user: { ...current.user, ...partial } };
      authService.setSession(next);
      setSession(next);
    },
    [authService],
  );

  const updateOrganizationInfo = useCallback<AuthContextType['updateOrganizationInfo']>(
    (partial) => {
      const current = authService.getSession();
      if (!current || !current.organization) return;
      const organizationId = current.organization.id;
      const next: AuthSession = {
        ...current,
        organization: { ...current.organization, ...partial },
        availableOrganizations:
          partial.name !== undefined
            ? current.availableOrganizations.map((org) =>
                org.id === organizationId ? { ...org, name: partial.name! } : org,
              )
            : current.availableOrganizations,
      };
      authService.setSession(next);
      setSession(next);
    },
    [authService],
  );

  const isAuthenticated = useMemo(
    () => !!token && !!session && Date.now() < new Date(session.expiresAt).getTime(),
    [token, session],
  );
  const requiresOnboarding = isAuthenticated && !session?.organization;

  const authValue: AuthContextType = {
    user: session?.user ?? null,
    organization: session?.organization ?? null,
    membership: session?.membership ?? null,
    availableOrganizations: session?.availableOrganizations ?? [],
    pendingInvitations: session?.pendingInvitations ?? [],
    token,
    isAuthenticated,
    loading,
    requiresOnboarding,
    login,
    registerOrganization,
    switchOrganization,
    acceptInvitation,
    logout,
    acquireEntraToken,
    updateUser,
    updateOrganizationInfo,
  };

  const nativeValue: NativeAuthContextType = {
    flow,
    startSignIn,
    startSignUp,
    submitOtp,
    resendOtp,
    cancelFlow,
  };

  return (
    <AuthContext.Provider value={authValue}>
      <NativeAuthContext.Provider value={nativeValue}>{children}</NativeAuthContext.Provider>
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function useNativeAuth(): NativeAuthContextType {
  const ctx = useContext(NativeAuthContext);
  if (!ctx) throw new Error('useNativeAuth must be used within AuthProvider');
  return ctx;
}
